/**
 * Keyed model-instance view pools (the `ShipViews` pattern of src/render/shipViews.ts for M2 pools): one view per
 * live owner slot (a wreck or part pool slot), keyed by what it shows (a ship def index, a component type). A
 * released view goes back to its key's free list and the next owner of that key reuses it, so views are built only
 * while a pool warms up (`created` stops growing). Views whose model has not loaded yet (deferred props) are
 * simply not drawn until it has.
 *
 * Frame protocol (allocation-free once warm, D-014): `beginViews` → `keepView` per live owner → `releaseStale` →
 * `acquireView` per live owner. Releasing before acquiring lets a view freed this frame (a wreck despawned) serve
 * an owner that appeared this frame (a new wreck of the same ship) instead of building another.
 */
import type { Object3D } from 'three/webgpu'
import type { ModelInstance } from '../../assets/loader'
import type { ShipLook } from '../../render/materials'

export interface PooledModelView {
  readonly key: number
  readonly instance: ModelInstance
  /** The ship look of a wreck view (null for props). */
  readonly look: ShipLook | null
  /** Owner slot shown, -1 while pooled. */
  owner: number
  /** Frame stamp of the last `acquireView`. */
  stamp: number
}

/** Builds a view of `key`, or null when its model is not loaded yet. Allocates (warm-up only). */
export type BuildModelView = (key: number) => PooledModelView | null

export interface ModelViewPool {
  readonly parent: Object3D
  /** Owner slot → its view. */
  readonly byOwner: (PooledModelView | null)[]
  /** Live views (unordered). */
  readonly live: PooledModelView[]
  /** Pooled views per key. */
  readonly free: PooledModelView[][]
  /** Every view built so far. */
  readonly all: PooledModelView[]
  readonly build: BuildModelView
  stamp: number
}

export function createModelViewPool(parent: Object3D, ownerCapacity: number, keyCount: number, build: BuildModelView): ModelViewPool {
  const free: PooledModelView[][] = []
  for (let k = 0; k < keyCount; k++) free.push([])
  return {
    parent,
    byOwner: new Array<PooledModelView | null>(ownerCapacity).fill(null),
    live: [],
    free,
    all: [],
    build,
    stamp: 0,
  }
}

/** Views built so far (live + pooled). */
export function createdViews(pool: ModelViewPool): number {
  return pool.all.length
}

/** Starts a frame. */
export function beginViews(pool: ModelViewPool): void {
  pool.stamp++
}

/** Builds a new view of `key` (warm-up; allocates). */
function growView(pool: ModelViewPool, key: number): PooledModelView | null {
  const v = pool.build(key)
  if (v) pool.all.push(v)
  return v
}

export function releaseView(pool: ModelViewPool, view: PooledModelView): void {
  if (view.owner >= 0 && pool.byOwner[view.owner] === view) pool.byOwner[view.owner] = null
  view.owner = -1
  const i = pool.live.indexOf(view)
  if (i >= 0) {
    pool.live[i] = pool.live[pool.live.length - 1]
    pool.live.pop()
  }
  view.instance.root.removeFromParent()
  pool.free[view.key].push(view)
}

/** Marks the view of owner slot `owner` as still in use this frame when it shows `key`. */
export function keepView(pool: ModelViewPool, owner: number, key: number): void {
  const v = pool.byOwner[owner]
  if (v && v.key === key) v.stamp = pool.stamp
}

/** The view of owner slot `owner` showing `key` this frame, or null while the key's model is not loaded. */
export function acquireView(pool: ModelViewPool, owner: number, key: number): PooledModelView | null {
  let v = pool.byOwner[owner]
  if (v && v.key !== key) {
    releaseView(pool, v)
    v = null
  }
  if (!v) {
    v = pool.free[key].pop() ?? growView(pool, key)
    if (!v) return null
    v.owner = owner
    pool.byOwner[owner] = v
    pool.live.push(v)
    pool.parent.add(v.instance.root)
  }
  v.stamp = pool.stamp
  return v
}

/** Releases every live view not acquired since `beginViews` (its owner is gone). */
export function releaseStale(pool: ModelViewPool): void {
  const live = pool.live
  for (let i = live.length - 1; i >= 0; i--) {
    if (live[i].stamp !== pool.stamp) releaseView(pool, live[i])
  }
}

export function disposeModelViewPool(pool: ModelViewPool): void {
  for (let i = pool.live.length - 1; i >= 0; i--) releaseView(pool, pool.live[i])
  for (let i = 0; i < pool.all.length; i++) pool.all[i].look?.dispose()
  pool.all.length = 0
  for (let k = 0; k < pool.free.length; k++) pool.free[k].length = 0
}
